<template>
  <div class="pause-body">
    <h2 class="h3 mb-2"><b>Press pause, not stop.</b></h2>
    <p class="text-muted mb-4">
      Not able to head out on trail at the minute? Pause your subscription until you're back in action.
    </p>

    <div class="pause-body__chips mb-4">
      <button
        v-for="option in PAUSE_DURATIONS"
        :key="option.months"
        type="button"
        class="pause-chip"
        :class="{ 'pause-chip--selected': option.months === modelValue }"
        @click="$emit('update:modelValue', option.months)"
      >
        <span class="pause-chip__label">{{ option.label }}</span>
        <span class="pause-chip__subtitle">pause</span>
      </button>
    </div>

    <div class="card pause-body__resume">
      <div class="card-body d-flex align-items-center gap-3">
        <span class="material-icons pause-body__resume-icon">event</span>
        <div>
          <p class="mb-0"><b>Adds {{ durationLabel }} to your renewal date</b></p>
          <p class="text-muted mb-0 small">
            You keep PRO+ until your current period ends, then it pauses and you're not charged. Resume any
            time from settings.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { PAUSE_DURATIONS, pauseDurationLabel } from "../subscriptionLogic";

const props = defineProps<{ modelValue: number }>();
defineEmits<{ (e: "update:modelValue", months: number): void }>();

// The pause starts when the current period ends, and only the backend knows that
// date — so describe the pause by its length rather than guessing a resume date.
const durationLabel = computed(() => pauseDurationLabel(props.modelValue));
</script>

<style scoped lang="scss">
.pause-body {
  &__chips {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  &__resume-icon {
    color: var(--yellow, #ffd700);
    font-size: 2rem;
  }
}

.pause-chip {
  flex: 1 1 0;
  min-width: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0.5rem;
  border-radius: 12px;
  border: 2px solid var(--gray-border, #434343);
  background: transparent;
  color: inherit;
  cursor: pointer;

  &__label {
    font-weight: 700;
    font-size: 1.1rem;
  }

  &__subtitle {
    color: var(--text-muted, #b2b2b2);
    font-size: 0.85rem;
  }

  &--selected {
    border-color: var(--yellow, #ffd700);
    background-color: rgba(255, 215, 0, 0.12);
  }
}
</style>
